import { ProcessEventTriggerResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerResponse';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import {
  namedKeyLabel,
  orchestrationEventUrl,
  orchestrationProcessUrl,
  orchestrationSubscriptionUrl,
} from '@app/pages/admin/orchestration-identity.util';
import {
  ProcessEventChipView,
  publishedEventsForProcess,
  sourceEventKeysByProcess,
} from '@app/pages/admin/orchestration-process-events.util';
import { canonicalProcessKey } from '@app/pages/admin/orchestration-process-keys.util';

export interface ProcessCatalogItem {
  key: string;
  displayName?: string;
  description?: string;
  version: number;
  maxConcurrentAssignments?: number;
  requiredWorkerTags?: string[];
  requiredWorkerTagLabel?: string;
}

export function requiredWorkerTagLabel(tags: readonly string[] | null | undefined): string {
  return (tags ?? []).map(tag => tag.trim()).filter(tag => tag.length > 0).join(', ');
}

export interface ProcessSubscriptionView {
  id: string;
  name: string;
  cronExpression: string;
  isActive: boolean;
  url: string | null;
}

export interface ProcessActivitySnapshot {
  lastSucceededLabel: string | null;
  lastFailedLabel: string | null;
  hasError: boolean;
}

export interface ProcessFlowNodeView {
  key: string;
  label: string;
  description?: string;
  version: number;
  maxConcurrentAssignments?: number;
  requiredWorkerTags?: string[];
  requiredWorkerTagLabel?: string;
  url: string;
  subscriptions: ProcessSubscriptionView[];
  incomingAny: StandaloneProcessView['incomingAny'];
  publishedEvents: ProcessEventChipView[];
  activity?: ProcessActivitySnapshot | null;
}

export interface ProcessFlowEdgeView {
  id: string;
  eventKey: string;
  eventLabel: string;
  eventUrl: string | null;
  triggerName: string;
  isActive: boolean;
  source: ProcessFlowNodeView;
  target: ProcessFlowNodeView;
}

export interface ProcessFlowStepView {
  node: ProcessFlowNodeView;
  incomingStarts: ProcessSubscriptionView[];
  outgoing: ProcessFlowEdgeView[];
}

export interface ProcessFlowView {
  id: string;
  linear: boolean;
  nodes: ProcessFlowNodeView[];
  steps: ProcessFlowStepView[];
  edges: ProcessFlowEdgeView[];
}

export interface StandaloneProcessView extends ProcessCatalogItem {
  incomingAny: Array<{ eventKey: string; eventLabel: string; eventUrl: string | null; isActive: boolean }>;
  publishedEvents: ProcessEventChipView[];
  subscriptions: ProcessSubscriptionView[];
  activity?: ProcessActivitySnapshot | null;
}

export interface ProcessCatalogView {
  flows: ProcessFlowView[];
  standalone: StandaloneProcessView[];
}

export function buildProcessCatalog(
  processes: ProcessCatalogItem[] | null | undefined,
  triggers: ProcessEventTriggerResponse[] | null | undefined,
  events: Array<{ key: string; displayName?: string }> | null | undefined,
  subscriptions?: ProcessSubscriptionResponse[] | null,
): ProcessCatalogView {
  const subscriptionsByKey = indexSubscriptions(subscriptions);
  const extraEventKeys = sourceEventKeysByProcess(triggers);
  const definitions = new Map<string, ProcessCatalogItem>();
  for (const process of processes ?? []) {
    const key = canonicalProcessKey(process.key);
    if (!definitions.has(key)) {
      definitions.set(key, {
        ...process,
        key,
        requiredWorkerTagLabel: requiredWorkerTagLabel(process.requiredWorkerTags),
      });
    }
  }

  const linkedEdges: ProcessFlowEdgeView[] = [];
  const incomingAny = new Map<string, StandaloneProcessView['incomingAny']>();
  for (const trigger of triggers ?? []) {
    const eventKey = trigger.eventKey?.trim() ?? '';
    const sourceKey = trigger.sourceProcessDefinitionKey?.trim() ?? '';
    const targetKey = trigger.targetProcessDefinitionKey?.trim() ?? '';
    if (!eventKey || !targetKey) {
      continue;
    }
    const eventLabel = namedKeyLabel(events, eventKey) || eventKey;
    const canonicalTarget = canonicalProcessKey(targetKey);
    if (!sourceKey) {
      const items = incomingAny.get(canonicalTarget) ?? [];
      items.push({
        eventKey,
        eventLabel,
        eventUrl: orchestrationEventUrl(eventKey),
        isActive: trigger.isActive !== false,
      });
      incomingAny.set(canonicalTarget, items);
      continue;
    }
      const source = toNode(canonicalProcessKey(sourceKey), definitions, subscriptionsByKey, events, extraEventKeys);
      const target = toNode(canonicalTarget, definitions, subscriptionsByKey, events, extraEventKeys);
    linkedEdges.push({
      id: trigger.id ?? `${source.key}:${eventKey}:${target.key}`,
      eventKey,
      eventLabel,
      eventUrl: orchestrationEventUrl(eventKey),
      triggerName: trigger.name?.trim() || eventLabel,
      isActive: trigger.isActive !== false,
      source,
      target,
    });
  }

  const parent = new Map<string, string>();
  const find = (key: string): string => {
    const current = parent.get(key) ?? key;
    if (current === key) {
      return key;
    }
    const root = find(current);
    parent.set(key, root);
    return root;
  };
  const union = (left: string, right: string): void => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) {
      parent.set(leftRoot, rightRoot);
    }
  };
  for (const edge of linkedEdges) {
    parent.set(edge.source.key, parent.get(edge.source.key) ?? edge.source.key);
    parent.set(edge.target.key, parent.get(edge.target.key) ?? edge.target.key);
    union(edge.source.key, edge.target.key);
  }

  const buckets = new Map<string, ProcessFlowEdgeView[]>();
  for (const edge of linkedEdges) {
    const root = find(edge.source.key);
    const items = buckets.get(root) ?? [];
    items.push(edge);
    buckets.set(root, items);
  }

  const linkedKeys = new Set<string>();
  const eventFlows = [...buckets.values()].map(edges => toFlow(edges));
  for (const flow of eventFlows) {
    for (const node of flow.nodes) {
      linkedKeys.add(node.key);
    }
  }

  const cronFlows: ProcessFlowView[] = [];
  const standalone: StandaloneProcessView[] = [];
  for (const process of definitions.values()) {
    if (linkedKeys.has(process.key)) {
      continue;
    }
    const node: ProcessFlowNodeView = {
      ...toNode(process.key, definitions, subscriptionsByKey, events, extraEventKeys),
      incomingAny: incomingAny.get(process.key) ?? [],
    };
    if (node.subscriptions.length) {
      cronFlows.push({
        id: `cron:${node.key}`,
        linear: true,
        nodes: [node],
        steps: [{
          node,
          incomingStarts: node.subscriptions,
          outgoing: [],
        }],
        edges: [],
      });
      continue;
    }
    standalone.push({
      ...process,
      incomingAny: incomingAny.get(process.key) ?? [],
      publishedEvents: publishedEventsForProcess(process.key, events, extraEventKeys.get(process.key) ?? []),
      subscriptions: [],
    });
  }

  const flows = [...eventFlows, ...cronFlows]
    .sort((left, right) => left.nodes[0]?.key.localeCompare(right.nodes[0]?.key ?? '') ?? 0);

  return { flows, standalone };
}

function toFlow(edges: ProcessFlowEdgeView[]): ProcessFlowView {
  const nodes = uniqueNodes(edges);
  const path = linearize(nodes, edges);
  return {
    id: nodes.map(node => node.key).sort().join('|'),
    linear: !!path,
    nodes: path?.map(step => step.node) ?? nodes,
    steps: path ?? nodes.map(node => ({
      node,
      incomingStarts: node.subscriptions,
      outgoing: [],
    })),
    edges,
  };
}

function linearize(
  nodes: ProcessFlowNodeView[],
  edges: ProcessFlowEdgeView[],
): ProcessFlowStepView[] | null {
  const forwards = edges.filter(edge => edge.source.key !== edge.target.key);
  if (forwards.length === 0) {
    return null;
  }
  const outgoing = new Map<string, Set<string>>();
  const incoming = new Map<string, Set<string>>();
  const between = new Map<string, ProcessFlowEdgeView[]>();
  for (const edge of forwards) {
    addNeighbor(outgoing, edge.source.key, edge.target.key);
    addNeighbor(incoming, edge.target.key, edge.source.key);
    const pair = `${edge.source.key}\0${edge.target.key}`;
    const items = between.get(pair) ?? [];
    items.push(edge);
    between.set(pair, items);
  }
  if ([...outgoing.values()].some(targets => targets.size > 1)
    || [...incoming.values()].some(sources => sources.size > 1)) {
    return null;
  }
  const starts = nodes.filter(node => (incoming.get(node.key)?.size ?? 0) === 0);
  if (starts.length !== 1) {
    return null;
  }
  const ordered: ProcessFlowNodeView[] = [];
  const seen = new Set<string>();
  let current: ProcessFlowNodeView | undefined = starts[0];
  while (current && !seen.has(current.key)) {
    ordered.push(current);
    seen.add(current.key);
    const nextKey: string | undefined = [...(outgoing.get(current.key) ?? [])][0];
    current = nextKey ? nodes.find(node => node.key === nextKey) : undefined;
  }
  if (ordered.length !== nodes.length) {
    return null;
  }
  return ordered.map((node, index) => {
    const next = ordered[index + 1];
    return {
      node,
      incomingStarts: node.subscriptions,
      outgoing: next ? (between.get(`${node.key}\0${next.key}`) ?? []) : [],
    };
  });
}

function uniqueNodes(edges: ProcessFlowEdgeView[]): ProcessFlowNodeView[] {
  const nodes = new Map<string, ProcessFlowNodeView>();
  for (const edge of edges) {
    nodes.set(edge.source.key, edge.source);
    nodes.set(edge.target.key, edge.target);
  }
  return [...nodes.values()].sort((left, right) => left.key.localeCompare(right.key));
}

function toNode(
  key: string,
  definitions: Map<string, ProcessCatalogItem>,
  subscriptionsByKey: Map<string, ProcessSubscriptionView[]>,
  events: Array<{ key: string; displayName?: string }> | null | undefined,
  extraEventKeys: Map<string, string[]>,
): ProcessFlowNodeView {
  const process = definitions.get(key);
  return {
    key,
    label: process?.displayName?.trim() || key,
    description: process?.description,
    version: process?.version ?? 1,
    maxConcurrentAssignments: process?.maxConcurrentAssignments,
    requiredWorkerTags: process?.requiredWorkerTags,
    requiredWorkerTagLabel: process?.requiredWorkerTagLabel ?? requiredWorkerTagLabel(process?.requiredWorkerTags),
    url: orchestrationProcessUrl(key) ?? `/admin/orchestration/processes/${key}`,
    subscriptions: subscriptionsByKey.get(key) ?? [],
    incomingAny: [],
    publishedEvents: publishedEventsForProcess(key, events, extraEventKeys.get(key) ?? []),
  };
}

function indexSubscriptions(
  items: ProcessSubscriptionResponse[] | null | undefined,
): Map<string, ProcessSubscriptionView[]> {
  const byKey = new Map<string, ProcessSubscriptionView[]>();
  for (const item of items ?? []) {
    const key = canonicalProcessKey(item.processDefinitionKey ?? '');
    if (!key) {
      continue;
    }
    const list = byKey.get(key) ?? [];
    list.push({
      id: item.id ?? `${key}:${item.name ?? ''}:${item.cronExpression ?? ''}`,
      name: item.name?.trim() || item.cronExpression || '—',
      cronExpression: item.cronExpression ?? '—',
      isActive: item.isActive !== false,
      url: orchestrationSubscriptionUrl(item.id),
    });
    byKey.set(key, list);
  }
  for (const list of byKey.values()) {
    list.sort((left, right) => {
      if (left.isActive !== right.isActive) {
        return left.isActive ? -1 : 1;
      }
      return left.name.localeCompare(right.name);
    });
  }
  return byKey;
}

function addNeighbor(map: Map<string, Set<string>>, from: string, to: string): void {
  const items = map.get(from) ?? new Set<string>();
  items.add(to);
  map.set(from, items);
}
